import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { cn } from "../utils/cn";

interface GhostPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  published_at: string;
  reading_time: number;
  feature_image?: string;
}

export default function Blog() {
  const [posts, setPosts] = useState<GhostPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ghostUrl, setGhostUrl] = useState<string>("");

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Fetch posts from our server API
        const response = await fetch("/api/blog/posts");

        if (!response.ok) {
          throw new Error("Failed to fetch posts from blog API");
        }

        const data = await response.json();
        setPosts(data.posts || []);
        setGhostUrl(data.ghostUrl || "");
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        console.error("Error fetching blog posts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  if (loading) {
    return (
      <section id="blog" className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg w-64 mx-auto mb-8"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-lg w-96 mx-auto mb-12"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-gray-200 dark:bg-gray-700 h-80 rounded-lg"
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="blog" className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-oxanium font-bold mb-6 text-black dark:text-white">
              Latest from Our Blog
            </h2>
            <p className="text-lg text-black/70 dark:text-white/70">
              Unable to load blog posts at this time. Please try again later.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="blog" className="py-24 relative overflow-hidden">
      {/* Top fade from previous section */}
      <div className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-gradient-to-b from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_bottom,black,transparent)]" />

      {/* Light mode theme background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white dark:hidden" />
      <div className="absolute inset-0 [background:radial-gradient(60%_40%_at_50%_0%,rgba(233,92,73,0.05),transparent_60%)] dark:hidden" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center"
        >
          {/* Section header */}
          <motion.div variants={itemVariants} className="mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-oxanium font-bold mb-6 text-black dark:text-white">
              Latest from Our Blog
            </h2>
            <p className="text-lg sm:text-xl text-black/70 dark:text-white/70 max-w-3xl mx-auto leading-relaxed">
              Stay updated with the latest insights, tutorials, and
              announcements from our team
            </p>
          </motion.div>

          {/* Blog posts grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.map((post) => (
              <motion.a
                key={post.id}
                href={`${ghostUrl}/${post.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                variants={itemVariants}
                className="group bg-white dark:bg-[#0a0d16] rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden block"
                whileHover={{ y: -5 }}
              >
                {/* Featured image */}
                {post.feature_image && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={post.feature_image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}

                <div className="p-6">
                  {/* Post title */}
                  <h3 className="text-xl font-bold mb-3 text-black dark:text-white group-hover:text-[#e95c49] transition-colors duration-200">
                    {post.title}
                  </h3>

                  {/* Post excerpt */}
                  <p className="text-black/70 dark:text-white/70 mb-6 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Read more indicator */}
                  <div className="flex items-center gap-2 text-[#e95c49] font-medium">
                    <span>Read More</span>
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </div>
                </div>
              </motion.a>
            ))}
          </div>

          {/* View all posts button */}
          {posts.length > 0 && (
            <motion.div variants={itemVariants} className="mt-12 text-center">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <a
                  href={ghostUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "inline-flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg",
                    "text-white",
                    "shadow-lg hover:shadow-xl",
                    "transition-all duration-300"
                  )}
                  style={{
                    backgroundColor: "#e95c49",
                    boxShadow: "0 10px 25px rgba(233, 92, 73, 0.25)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#e95c49";
                    e.currentTarget.style.boxShadow =
                      "0 25px 50px rgba(233, 92, 73, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#e95c49";
                    e.currentTarget.style.boxShadow =
                      "0 10px 25px rgba(233, 92, 73, 0.25)";
                  }}
                >
                  View All Posts
                  <ArrowRightIcon className="h-5 w-5" />
                </a>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Bottom fade to next section */}
      <div className="pointer-events-none absolute inset-x-0 -bottom-16 h-16 bg-gradient-to-t from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_top,black,transparent)]" />

      {/* Floating elements */}
      <motion.div
        className="absolute top-20 left-20 w-32 h-32 rounded-full bg-white/10 blur-2xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.3, 0.1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute bottom-20 right-20 w-24 h-24 rounded-full bg-white/10 blur-2xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </section>
  );
}
